<template>
  <div>
    <div id="map-wrapper" :class="size" :style="wrapperStyles">
      <div :id="id" style="height: 100%; width: 100%;">
        <div id="google-map-container" class="google-map"></div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import GoogleMapOptions from '../GoogleMapOptions'
import googleMaps from './googleMaps'
import bus from '@/plugins/bus'
import debounce from 'lodash.debounce'

export default {
  name: 'gMap',
  data () {
    return {
      mapObject: null,
      mounted: false,
      noMap: false,
      noMapMessage: '',
      viewOptions: [],
      mapSpace: null,
      options: {
        mapTypeId: null,
        center: {
          lat: null,
          lng: null
        },
        disableDefaultUI: true,
        rotateControl: false,
        scrollWheel: false,
        streetViewControl: false,
        zoomControl: true,
        zoom: 17,
        mapTypeControlOptions: {
          style: null,
          position: null
        },
        fullscreenControlOptions: {
          position: null
        },
        zoomControlOptions: {
          position: null
        },
      },
      overlay: true,
      loading: true,
      mapDefaults: {
        bounds: null
      },
      animateZoom: {
        zoomIn: 21,
        zoomOut: 15
      },
      markersArray: [],
      HS_HO: {
        center: {
          lat: 51.513372,
          lng: -0.188446
        }
      },
      element: null,
      propertyLatLngObj: null,
      propertyMarker: null,
      map: {
        instance: null
      },
      debouncedBoundsEvent: null,
    }
  },
  props: {
    mapTypeId: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    // Currently css support for classes small, large, x-large
    size: {
      type: String,
      required: true
    },
    wrapperStyles: {
      type: Object,
      required: false,
      default () {
        return {}
      }
    },
    // used to customise map options center, and place property marker - passed as a prop to **satview/streetview** components
    lat: {
      type: Number,
      required: true
    },
    // used to customise map options center, and place property marker - passed as a prop to **satview/streetview** components
    lng: {
      type: Number,
      required: true
    },
    // used to customise map options
    scrollWheel: {
      type: Boolean,
      required: false
    },
    // used to customise map options
    disableDefaultUI: {
      type: Boolean,
      required: false
    },
    // used to customise map options
    rotateControl: {
      type: Boolean,
      required: false
    },
    // used to customise map options
    zoomControl: {
      type: Boolean,
      required: false
    },
    // Set final zoom level, this is the level started at and animated back to after zoom in
    zoom: {
      type: Number,
      required: false
    },
    // Set zoom button position
    zoomControlPosition: {
      type: String,
      required: false
    },
    hasZoomEvent: {
      type: Boolean,
      required: false
    },
    // e.g. {north: 51.52089, south: 51.32504, east: 0.02263, west: -0.52305}
    // or state object sataelliteview.bounds
    bounds: {
      type: Object,
      required: false,
      default: () => null
    },
    // Used to plot multiple markers on the map
    markers: {
      type: Array,
      required: false
    },
  },
  beforeMount () {
    let filteredProps = this.filterProps()
    this.mapObject = { ...filteredProps }
  },
  created () {
    this.debouncedBoundsEvent = debounce(() => this.updateBounds(), 500)
  },
  mounted () {
    this.init()
  },
  methods: {
    async init () {
      await googleMaps.load()
      if (this.propertyMarker) this.propertyMarker.setMap(null)
      this.mapSpace = document.getElementById(this.id)
      this.setMapConstructorParams()

      this.$nextTick(() => {
        this.generateSatelliteViewMap()
      })
    },
    filterProps() {
      let result = {}, key;
      for (key in this.$props) {
        if (this.$props.hasOwnProperty(key) && this.$props[key] !== undefined) {
          result[key] = this.$props[key]
        }
      }
      return result;
    },
    setMapConstructorParams () {
      if (!this.element) {
        this.element = document.getElementById('google-map-container')
        this.element.setAttribute('id', this.mapTypeId)
        this.element.setAttribute('data-cy', this.mapTypeId)
      }

      this.options.mapTypeControlOptions.style = window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR
      this.options.mapTypeControlOptions.position = window.google.maps.ControlPosition.LEFT_TOP
      this.options.fullscreenControlOptions.position = window.google.maps.ControlPosition.RIGHT_TOP

      this.options.mapTypeId =  this.mapTypeId
      this.options.center.lat = this.mapObject.lat
      this.options.center.lng = this.mapObject.lng
      this.options.zoom = this.zoom || this.options.zoom
      // this.animateZoom.zoomOut = this.zoom || this.animateZoom.zoomOut
      this.mapDefaults.bounds = this.bounds || this.mapDefaults.bounds

      this.getZoomControlPosition(this.zoomControlPosition)
    },
    async generateSatelliteViewMap () {
      this.propertyLatLngObj = new window.google.maps.LatLng(this.options.center)

      try {
        const paths = await this.getMap()
        this.overlay = false
        this.loading = false

        await this.addPropertyMarker()
        this.$emit('mapLoaded')
        // if (window.Cypress) {   // only when testing
        //   this.fitBounds()
        // }
      } catch (e) {
        this.propertyLatLngObj = new window.google.maps.LatLng(this.HS_HO.center)
        this.map.instance.setZoom(17)
        this.map.instance.setCenter(this.propertyLatLngObj)
        this.map.instance.setMapTypeId('roadmap')
        this.noMapMessage = e
        this.overlay = true
        this.loading = false
        this.noMap = true
      }
    },
    getMap () {
      return new Promise(async (resolve, reject) => {
        if (!this.map.instance) {
          console.log('A new sat view map instance was created')
          this.map.instance = new window.google.maps.Map(this.element)
        }
        this.map.instance.setOptions({ styles: GoogleMapOptions, ...this.options })

        await this.addInitialMarkers()

        this.mapSpace.append(this.element)
        resolve()
      })
    },
    addInitialMarkers () {
        return new Promise( (resolve) => {
            // ADD MARKERS
            if (this.markers) this.addMarkers({ markers: this.markers })
            resolve(true)
        })
    },
    addPropertyMarker () {
      return new Promise((resolve) => {
        this.addSingleMarker(resolve)
      })
    },
    addSingleMarker (resolve = false, center = false) {
      if (!this.propertyMarker) {
        this.propertyMarker = new window.google.maps.Marker({
          position: (center) ? center : this.options.center,
          icon: {
            url: require('@/assets/img/markers/pins/home.svg'),
            scaledSize: new window.google.maps.Size(40, 40),
          },
          animation: window.google.maps.Animation.DROP
        })
        this.propertyMarker.set('optimized', false)
        this.propertyMarker.setMap(this.map.instance)
        this.markersArray.push(this.propertyMarker);
      } else {
        this.propertyMarker.setPosition((center) ? center : this.propertyLatLngObj)
        this.propertyMarker.setAnimation(window.google.maps.Animation.DROP)
        this.propertyMarker.set('optimized', false)
        this.propertyMarker.setMap(this.map.instance)
      }
      if (resolve) {
        resolve(true)
      }
    },
    getMarkerContent (marker) {
      const mapper = {
        'underoffer': 'under-offer',
        'onmarket': 'on-the-market',
        sold: 'recently-sold',
        'bluepin': 'blue-pin',
      }

      const fileName = mapper[marker.icon] ?? 'black-circle-pin'

      return `${require(`@/assets/img/markers/pins/${fileName}.svg`)}`
    },
    addMarkers (markerObj) {
      let { markers } = markerObj

      markers.forEach((marker, i) => {
        let position
        if (marker.lat && marker.lon) {
            position = new window.google.maps.LatLng(marker.lat, marker.lon)
            let markerInstance = new google.maps.Marker({
              position,
              icon: {
                url: this.getMarkerContent(marker),
                scaledSize: new window.google.maps.Size(30, 30)
              },
            })
            this.markersArray.push(markerInstance);
            this.markersArray[i].setMap(this.map.instance);
            if (marker.icon) {
              this.boldSectionOnHover(markerInstance, marker)
            }
            markerInstance = null
        }
      })
      return this
    },
    clearMarkers () {
      for (let i = 0; i < this.markersArray.length; i++ ) {
        this.markersArray[i].setMap(null);
      }
      this.markersArray.length = 0;
    },
    getZoomControlPosition (value) {
      const gmControlPosition = window.google.maps.ControlPosition
      const positions = {
        'left-bottom': 'LEFT_BOTTOM',
        'left-top': 'LEFT_TOP'
      }
      const defaultValue = positions['left-bottom']
      this.options.zoomControlOptions.position = gmControlPosition[positions[value] ?? defaultValue]
    },
    boldSectionOnHover (instance, marker) {
      instance.addListener('mouseover', () => {
        const largeIcon = {
          url: this.getMarkerContent(marker),
          scaledSize: new google.maps.Size(45, 45)
        }
        instance.setIcon(largeIcon);
        bus.emit('markerPosition', { lat: marker.lat, lon: marker.lon })
      })
      instance.addListener('mouseout', () => {
        instance.setIcon(this.getMarkerContent(marker));
        bus.emit('markerPosition', { lat: 0, lon: 0 })
      })
    },
    addBoundsEventListener () {
      this.BoundsChangedEvent = google.maps.event.addListener(
          this.map.instance,
          'bounds_changed',
          this.debouncedBoundsEvent
      )
    },
    updateBounds () {
      const bounds =  this.map.instance.getBounds()
      const east = bounds.getNorthEast().lng()
      const north = bounds.getNorthEast().lat()
      const south =  bounds.getSouthWest().lat()
      const west = bounds.getSouthWest().lng()

      console.log('bounds_changed')

      bus.emit('boundsChanged', {
        east,
        north,
        south,
        west,
      })
    },
    fitBounds () {
      this.mapDefaults.bounds = new window.google.maps.LatLngBounds()
      this.markersArray.forEach(marker => {
        this.mapDefaults.bounds.extend(marker.position)
      })
      this.map.instance.fitBounds(this.mapDefaults.bounds)
    },
    checkEventAndFitBounds () {
      this.BoundsChangedEvent && google.maps.event.removeListener(this.BoundsChangedEvent)
      if (!this.markers.length) {
        this.map.instance.fitBounds(this.bounds)
        return
      }

      this.fitBounds()
      setTimeout(() => {
        this.hasZoomEvent && this.addBoundsEventListener()
      })
    }
  },
  watch:{
    markers () {
      this.clearMarkers()
      this.addPropertyMarker()
      this.addInitialMarkers()
    }
  },
}
</script>

<style scoped lang="scss">
  ::v-deep#map-wrapper {
    position: relative;

    &.small {
      height: 396px;
    }

    &.large {
      height: 600px;
    }

    &.x-large {
      height: 800px;
    }

    .gm-iv-address {
      display:none!important;
    }

    .google-map {
      width: 100%;
      height: 100%;
      margin: 0 auto;
      background: transparent;
    }

    .map-loading-overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: 0;
      background: #000;
      opacity: .8;
      z-index: 1000;
    }

    .map-failed-overlay {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: 0;
      background: transparent;
      z-index: 1000;
    }

    .info-window {
      max-width: 654px!important;
      max-height: 669px!important;
      font-family: fieldwork-hum;
      h4 {
        font-size: 16px;
        font-weight: 600;
      }
    }
    .info-window ul {
      max-height: 200px !important;
      overflow-x: auto;
      padding-left: 15px;

      li.list-group-item {
        border: 0;
        border-bottom: 1px solid #E5E5E5;
        padding-left: 0;
        position: relative;
        a {
          color: #FF4B51;
          &:hover {
            color: darken(#FF4B51, 7);
          }
        }
        &.market {
          &:before {
            content: '•';
            position: absolute;
            color: #4FD088;
            font-size: 30px;
            top: calc(50% - 15px);
            left: -15px;
          }
        }
      }

      &::-webkit-scrollbar {
        width: 12px;
      }

      &::-webkit-scrollbar-track {
        box-shadow: inset 0 0 14px 14px transparent;
        border: solid 4px transparent;
      }

      &::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 14px 14px #bbbbbe;
        border: solid 4px transparent;
        border-radius: 14px;
      }
    }
    .sat-view-map-space {
      height: 100%;
      width: 100%;
      position: absolute;
    }
    .property-pin-image {
      height: auto;
      max-width: 100%;
      &.home {
        width: 55px;
      }
    }
    .property-marker-info {
      margin-top: 15px;
      display: flex;
      align-items: flex-start;
      min-width: 255px;
      &.no-image {
        min-width: 200px;
      }
      img {
        height: 60px;
        width: 100%;
        max-width: 90px !important;
        margin-right: 10px;
      }
      .info-content {
        width: 100%;

        .info-block {
          margin-bottom: 6px;
        }

        .info-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          span {
            font-size: 14px;
            &:first-child{
              color: #000;
              font-weight: bold;
              margin-right: 25px;
            }
          }
        }

        .info-bottom {
          span {
            display: block;
            margin-bottom: 6px;
            color: #A7A7A7;
            font-size: 11px;
          }
        }

        .info-status {
          border-radius: 3px;
          font-size: 10px;
          padding: 5px 10px;
          white-space: nowrap;
          &.underoffer {
            color: #F7B500;
            background-color: #FBF7E5;
          }
          &.onmarket {
            color: #4FD088;
            background-color: #E5FBEE;
          }
          &.sold {
            color: #439FEA;
            background-color: #E8F5FF;
          }
        }
      }
    }
  }
</style>
